import React, { useEffect, useRef } from 'react';

import useSearchContracts from './hooks/useSearchContracts';
import useDownloadReportForm from '../../hooks/useDownloadReportForm';

import SearchInput from '../../../_ui/Form/SearchInput/SearchInput';
import Typography from '../../../_ui/Typography/Typography';
import Tag from '../../../_ui/Tag/Tag';
import SearchDropDown from '../components/SearchDropDown';

type UseDownloadReportFormReturnType = ReturnType<typeof useDownloadReportForm>;

interface Props extends Pick<UseDownloadReportFormReturnType, 'formData' | 'setFormData'> {
    errorMessage?: string;
}

export default function ContractSelector({ formData, setFormData, errorMessage }: Props) {
    const {
        searchTerm,
        setSearchTerm,
        dropdownOpen,
        setDropdownOpen,
        isLoading,
        contracts,
    } = useSearchContracts();

    const searchInputWrapperRef = useRef<HTMLDivElement | null>(null);

    const setContract = (contractId: number, contractName: string) => {
        const isAlreadySet = formData.contracts.find(({ id }) => (
            id === contractId
        ));

        setFormData({
            ...formData,
            contracts: isAlreadySet
                ? formData.contracts.filter(({ id }) => (
                    id !== contractId
                ))
                : [...formData.contracts, {
                    id: contractId,
                    name: contractName,
                }]
        })
    }

    const formStateContractIds = formData.contracts.map(({ id }) => id);
    const filteredContracts = contracts.filter(({ id }) => (
        !formStateContractIds.includes(id!)
    ));

    // On input focus, display the dropdown if the dropdown is hidden and there's a search term.
    const handleInputFocus = () => {
        console.log('focus');

        if (!dropdownOpen && searchTerm) {
            setDropdownOpen(true)
        }
    }

    // Closes the dropdown if the user clicks anywhere else on the page that's not part of the input or
    // dropdown.
    useEffect(() => {
        const handleCloseDropdownClick = (e: MouseEvent) => {
            const { target } = e;

            // If there's no input wrapper ref, no target clicked, or the target is not an HTML node.
            if (!searchInputWrapperRef.current || !target || target instanceof HTMLElement === false) {
                return;
            }

            // If the user clicked an item that's not inside the input ref, then we want to hide the dropdown.
            if (searchInputWrapperRef.current.contains(target) === false) {
                setDropdownOpen(false);
            }
        }

        // Only add the click event listener if there's a search term.
        if (searchTerm) {
            document.body.addEventListener('click', handleCloseDropdownClick, true);
        }

        // Always run the cleanup, will fail silently if there is no click event to remove.
        return () => {
            document.body.removeEventListener('click', handleCloseDropdownClick, true);
        }
    }, [searchTerm, setDropdownOpen]);

    return (
        <fieldset className="space-y-050">
            <legend className="type-small-caps">Contracts</legend>
            <div className="relative">
                <SearchInput
                    name="contract-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value || '')}
                    placeholder="Search by contract name"
                    onFocus={handleInputFocus}
                />
                {searchTerm ? (
                    <SearchDropDown
                        isLoading={isLoading}
                        items={filteredContracts}
                        noneFoundMessage={`No results found for ${searchTerm}`}
                        onSelect={setContract}
                    />
                ) : null}
            </div>
            {errorMessage
                ? <Typography as="div" variant="meta" color="error">{errorMessage}</Typography>
                : null}
            {formData.contracts.length ? (
                <div className="flex gap-025 flex-wrap max-w-[19rem]">
                    {formData.contracts.map((contract) => (
                        <Tag
                            key={contract.id}
                            size="small"
                            onClick={() => setContract(contract.id!, contract.name)}
                            icon="cross"
                            title={`Remove ${contract.name}`}
                        >
                            {contract.name}
                        </Tag>
                    ))}
                </div>
            ) : null}
        </fieldset>
    );
};
